import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Loader2 } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "../ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../ui/sheet";
import { Button } from "../ui/button";
import { Pill, StatePill } from "../Ui";
import TallySyncBadge from "../TallySyncBadge";
import VoucherDocumentActions from "../VoucherDocumentActions";
import { api, extractError } from "../../lib/api";
import { formatInr } from "../../lib/accountsSettlement";
import { formatListDate } from "../../lib/purchaseListMeta";
import { formatCurrency, formatQty } from "../../pages/purchase/purchaseReportShared";
import { toast } from "sonner";

function BriefRow({ label, value, emphasize = false }) {
  return (
    <div className="flex items-start justify-between gap-4 py-1.5 border-b border-border/50 last:border-0">
      <span className="text-xs text-muted-foreground shrink-0">{label}</span>
      <span className={`text-sm text-right tabular break-all ${emphasize ? "font-semibold text-foreground" : "text-foreground"}`}>
        {value ?? "—"}
      </span>
    </div>
  );
}

function moneyLines(lines = []) {
  return (lines || []).filter(
    (ln) => ln?.effect && !["weight", "info"].includes(ln.effect) && Number(ln.amount || 0) !== 0,
  );
}

function formatTdsInr(n) {
  return `₹${Number(n ?? 0).toLocaleString("en-IN", { maximumFractionDigits: 0, minimumFractionDigits: 0 })}`;
}

function tdsBreakdownLines(deductions = {}) {
  const fromLines = (deductions.lines || []).filter(
    (ln) => ln && (ln.tds_applicable || Number(ln.tds_amount || 0) > 0),
  );
  if (fromLines.length) return fromLines;
  const code = deductions.payment_code || deductions.tds_payment_code;
  if (code || deductions.tds_total) {
    return [
      {
        payment_code: code,
        taxable_amount: deductions.taxable_amount ?? deductions.tds_taxable,
        tds_pct: deductions.tds_pct,
        tds_amount: deductions.tds_total,
        reason: deductions.title,
        label: deductions.title,
      },
    ];
  }
  return [];
}

function tdsSectionLabel(line) {
  const raw = String(line?.reason || line?.label || "").trim();
  if (!raw) return line?.nature_code ? String(line.nature_code).replace(/_/g, " ") : "—";
  const act = raw.match(/\(([^)]+)\)/);
  if (act?.[1]) return act[1];
  return raw.split("—")[0].trim().slice(0, 80);
}

function money(n) {
  return Math.round((Number(n) || 0) * 100) / 100;
}

const TYPE_TONE = {
  MATERIAL: "info",
  PURCHASE: "info",
  FREIGHT: "warning",
  TDS: "default",
  CASH: "warning",
  PAYMENT: "warning",
  RECEIPT: "success",
  SALES: "info",
  JOURNAL: "default",
};

function VoucherBriefBody({
  voucher,
  voucherId,
  voucherCode,
  label,
  rowHint = null,
  ledgerContext = null,
}) {
  const vtype = String(voucher?.voucher_type || label || "").toUpperCase();
  const isFreight = vtype === "FREIGHT";
  const isTds = vtype === "TDS";
  const isCash = vtype === "CASH" || vtype === "RECEIPT";
  const deductions = voucher?.deductions || {};
  const mandi = deductions.mandi_weight || {};
  const materialLines = moneyLines(deductions.lines);
  const freightPayment = voucher?.freight_payment || deductions.freight_payment || {};
  const freightSettlement = voucher?.freight_settlement || deductions.freight_settlement || {};
  const freightSlip = voucher?.freight_slip || deductions.freight_slip || {};
  const cashLedger = deductions.cash_ledger || {};
  const tdsLines = isTds ? tdsBreakdownLines(deductions) : [];

  const freightAmountLines = [
    {
      label: "Freight (gross)",
      amount: freightSettlement.gross_freight ?? voucher?.gross_amount ?? rowHint?.freight_gross,
    },
    {
      label: "Other charges",
      amount: freightSettlement.other_charges ?? freightSlip.other_charges,
    },
    {
      label: "Short goods deduction",
      amount: -(Number(freightSettlement.shortage_deduction ?? freightSettlement.freight_deduction) || 0),
      hideIfZero: true,
    },
    {
      label: "Party advance to transporter",
      amount: -(Number(freightSettlement.party_advance ?? freightSettlement.advance ?? freightPayment.party_advance) || 0),
      hideIfZero: true,
    },
    {
      label: freightSettlement.tds_pct != null ? `TDS (${freightSettlement.tds_pct}%)` : "TDS",
      amount: -(Number(freightSettlement.tds_amount ?? freightPayment.tds_amount) || 0),
      hideIfZero: true,
    },
    {
      label: "Clerk / Bilty",
      amount: -(Number(freightSettlement.clerk_bilty_charge) || 0),
      hideIfZero: true,
    },
    {
      label: "Dala",
      amount: -(Number(freightSettlement.dala) || 0),
      hideIfZero: true,
    },
    {
      label: "Other deductions",
      amount: -(Number(freightSettlement.other_deductions) || 0),
      hideIfZero: true,
    },
  ].filter((ln) => {
    if (ln.amount == null || ln.amount === "" || Number.isNaN(Number(ln.amount))) return false;
    if (ln.hideIfZero && Number(ln.amount) === 0) return false;
    return true;
  });

  return (
    <div className="space-y-4">
      {ledgerContext ? (
        <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">On this ledger</p>
          <BriefRow label="Date" value={ledgerContext.date} />
          <BriefRow label="Particulars" value={ledgerContext.particulars} />
          <BriefRow label="Opposite ledger" value={ledgerContext.opposite_ledger} />
          <BriefRow
            label="Debit"
            value={money(ledgerContext.debit) > 0 ? formatInr(ledgerContext.debit) : "—"}
            emphasize={money(ledgerContext.debit) > 0}
          />
          <BriefRow
            label="Credit"
            value={money(ledgerContext.credit) > 0 ? formatInr(ledgerContext.credit) : "—"}
            emphasize={money(ledgerContext.credit) > 0}
          />
          <BriefRow label="Running balance" value={ledgerContext.running_balance_display} emphasize />
          {ledgerContext.payment_mode ? <BriefRow label="Mode" value={ledgerContext.payment_mode} /> : null}
          {ledgerContext.accounts_note ? <BriefRow label="Note" value={ledgerContext.accounts_note} /> : null}
        </div>
      ) : null}

      <div className="rounded-lg border border-border bg-muted/30 px-3 py-2 space-y-0.5">
        <BriefRow label="Party" value={voucher?.party_name || rowHint?.party_name} />
        <BriefRow label="Purchase" value={voucher?.purchase_code || rowHint?.purchase_code || ledgerContext?.purchase_code} />
        <BriefRow label="Variety" value={voucher?.variety} />
        {voucher?.contract_label ? <BriefRow label="Contract" value={voucher.contract_label} /> : null}
        {rowHint?.invoice_no ? <BriefRow label="Invoice" value={rowHint.invoice_no} /> : null}
        {voucher?.gate_pass_code ? <BriefRow label="Gate pass" value={voucher.gate_pass_code} /> : null}
        {voucher?.vehicle_number ? (
          <BriefRow label="Vehicle" value={String(voucher.vehicle_number).toUpperCase()} />
        ) : null}
      </div>

      {isTds ? (
        <div className="rounded-lg border border-border px-3 py-2 space-y-3">
          <div>
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">TDS deduction</p>
            <BriefRow label="Scope" value={deductions.tds_scope || deductions.title} />
            <BriefRow label="Parent voucher" value={deductions.parent_voucher_code} />
          </div>

          {tdsLines.length > 0 ? (
            <div className="space-y-3">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground">By section</p>
              {tdsLines.map((ln, idx) => (
                <div
                  key={`${ln.payment_code || ln.nature_code || "tds"}-${idx}`}
                  className="rounded-md border border-border/60 bg-muted/20 px-2.5 py-2"
                >
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <span className="text-xs font-semibold font-mono">
                      {ln.payment_code ? `Code ${ln.payment_code}` : ln.nature_code || "TDS"}
                    </span>
                    {ln.catch_up ? (
                      <span className="text-[10px] text-amber-700 dark:text-amber-300">catch-up</span>
                    ) : null}
                  </div>
                  <BriefRow label="Section" value={tdsSectionLabel(ln)} />
                  <BriefRow
                    label="Taxable amount"
                    value={Number(ln.taxable_amount || 0) > 0 ? formatCurrency(ln.taxable_amount) : "—"}
                  />
                  <BriefRow
                    label="Rate"
                    value={ln.tds_pct != null && Number(ln.tds_pct) > 0 ? `${Number(ln.tds_pct)}%` : "—"}
                  />
                  {Number(ln.current_gross || ln.gross_amount || 0) > 0 ? (
                    <BriefRow
                      label="Payment (gross)"
                      value={formatCurrency(ln.current_gross ?? ln.gross_amount)}
                    />
                  ) : null}
                  <BriefRow label="TDS deducted" value={formatTdsInr(ln.tds_amount)} emphasize />
                </div>
              ))}
            </div>
          ) : (
            <BriefRow label="Payment code" value={deductions.payment_code || deductions.tds_payment_code} />
          )}

          <BriefRow
            label="Total TDS"
            value={formatTdsInr(voucher?.net_payable ?? deductions.tds_total)}
            emphasize
          />
        </div>
      ) : isCash ? (
        <div className="rounded-lg border border-border px-3 py-2">
          <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Payment</p>
          <BriefRow label="Mode" value={deductions.payment_mode || voucher?.payment_mode} />
          <BriefRow label="Allocation" value={cashLedger.allocation_mode} />
          <BriefRow label="Instrument" value={cashLedger.instrument_no || cashLedger.cheque_no} />
          <BriefRow label="Bank" value={cashLedger.bank_name} />
          <BriefRow
            label="Amount"
            value={formatCurrency(voucher?.net_payable ?? rowHint?.net_amount)}
            emphasize
          />
        </div>
      ) : isFreight ? (
        <>
          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Freight weights</p>
            <BriefRow
              label="Load weight"
              value={formatQty(freightSettlement.load_weight_qtl ?? freightSettlement.party_net_qtl)}
            />
            <BriefRow
              label="Unload weight"
              value={formatQty(freightSettlement.unload_weight_qtl ?? freightSettlement.our_net_qtl)}
            />
            <BriefRow
              label="Freight billable"
              value={formatQty(freightSettlement.freight_billable_qtl ?? voucher?.billable_qtl)}
            />
            <BriefRow
              label="Shortage"
              value={formatQty(freightSettlement.shortage_qtl ?? freightSettlement.weight_diff_qtl)}
            />
            <BriefRow label="Allowed shortage" value={formatQty(freightSettlement.allowance_qtl)} />
            <BriefRow
              label="Net shortage"
              value={formatQty(freightSettlement.net_shortage_qtl ?? freightSettlement.weight_short_qtl)}
            />
            <BriefRow
              label="Freight rate"
              value={
                (freightSettlement.freight_rate_per_qtl ?? voucher?.rate_per_qtl) != null
                  ? `₹${Number(freightSettlement.freight_rate_per_qtl ?? voucher.rate_per_qtl).toLocaleString("en-IN")}/qtl`
                  : null
              }
            />
          </div>

          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Amount brief</p>
            {freightAmountLines.map((ln) => (
              <BriefRow key={ln.label} label={ln.label} value={formatCurrency(ln.amount)} />
            ))}
            <BriefRow
              label="Net freight payable"
              value={formatCurrency(
                freightSettlement.net_freight_payable
                ?? voucher?.net_payable
                ?? rowHint?.freight_net,
              )}
              emphasize
            />
          </div>

          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Payment split</p>
            <BriefRow label="Mode" value={freightPayment.mode || freightPayment.payment_mode} />
            <BriefRow
              label="Pay transporter"
              value={formatCurrency(
                freightPayment.payable_to_transporter
                ?? freightSettlement.net_freight_payable
                ?? voucher?.net_payable,
              )}
            />
            <BriefRow
              label="Paid by party"
              value={formatCurrency(freightPayment.party_paid_transporter ?? rowHint?.freight_paid_by_party)}
            />
            <BriefRow
              label="By BCPL"
              value={formatCurrency(freightPayment.we_pay_transporter ?? rowHint?.freight_paid_by_bcpl)}
            />
            {freightPayment.party_bill_amount > 0 && freightPayment.party_bill_effect ? (
              <BriefRow
                label="Supplier bill"
                value={`${freightPayment.party_bill_effect === "add" ? "Add" : "Deduct"} ${formatCurrency(freightPayment.party_bill_amount)}`}
              />
            ) : null}
          </div>
        </>
      ) : (
        <>
          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Weight & rate</p>
            <BriefRow label="Net qtl" value={formatQty(voucher?.net_qtl)} />
            <BriefRow label="Billable qtl" value={formatQty(voucher?.billable_qtl ?? mandi.billable_qtl)} />
            {mandi.weight_mode ? (
              <BriefRow label="Weight mode" value={String(mandi.weight_mode).replace(/_/g, " ")} />
            ) : null}
            <BriefRow
              label="Rate / qtl"
              value={voucher?.rate_per_qtl != null ? `₹${Number(voucher.rate_per_qtl).toLocaleString("en-IN")}` : null}
            />
            <BriefRow
              label="Effective rate"
              value={voucher?.effective_rate != null ? `₹${Number(voucher.effective_rate).toLocaleString("en-IN")}` : null}
            />
          </div>

          <div className="rounded-lg border border-border px-3 py-2">
            <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Amount brief</p>
            <BriefRow label="Gross" value={formatCurrency(voucher?.gross_amount ?? rowHint?.gross_amount)} />
            {(rowHint?.expenses || materialLines).length > 0 ? (
              <div className="mt-1 mb-1 space-y-0.5">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground">Lines</p>
                {(rowHint?.expenses?.length
                  ? rowHint.expenses
                  : materialLines.map((ln) => ({
                    label: ln.label || ln.code,
                    amount: ln.effect === "subtract" ? -Number(ln.amount || 0) : Number(ln.amount || 0),
                  }))
                ).map((exp, idx) => (
                  <BriefRow key={`${exp.label}-${idx}`} label={exp.label} value={formatCurrency(exp.amount)} />
                ))}
              </div>
            ) : null}
            <BriefRow
              label="Net payable"
              value={formatCurrency(voucher?.net_payable ?? rowHint?.net_amount)}
              emphasize
            />
          </div>
        </>
      )}

      {voucherId ? (
        <div className="flex flex-wrap items-center justify-between gap-3 pt-1 border-t border-border">
          <p className="text-xs text-muted-foreground">Print or download this voucher</p>
          <VoucherDocumentActions
            voucherId={voucherId}
            voucherCode={voucher?.code || voucherCode}
            label={label || (isFreight ? "Freight" : isTds ? "TDS" : "Material")}
          />
        </div>
      ) : null}
    </div>
  );
}

/**
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void, voucherId?: number|string, voucherCode?: string, label?: string, rowHint?: object|null, ledgerContext?: object|null, presentation?: 'dialog'|'sheet', partyId?: string|number|null }} props
 */
export function VoucherBriefDialog({
  open,
  onOpenChange,
  voucherId,
  voucherCode,
  label,
  rowHint = null,
  ledgerContext = null,
  presentation = "dialog",
  partyId = null,
}) {
  const [loading, setLoading] = useState(false);
  const [voucher, setVoucher] = useState(null);

  useEffect(() => {
    if (!open || !voucherId) {
      setVoucher(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/vouchers/${voucherId}`)
      .then((r) => {
        if (!cancelled) setVoucher(r.data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(extractError(err));
          setVoucher(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, voucherId]);

  const vtype = String(voucher?.voucher_type || label || ledgerContext?.voucher_type || "").toUpperCase();
  const title = voucher?.code || voucherCode || "Voucher";

  const body = loading ? (
    <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
      <Loader2 className="h-4 w-4 animate-spin" /> Loading voucher…
    </div>
  ) : (
    <>
      <VoucherBriefBody
        voucher={voucher}
        voucherId={voucherId}
        voucherCode={voucherCode}
        label={label}
        rowHint={rowHint}
        ledgerContext={ledgerContext}
      />
      {partyId ? (
        <div className="flex flex-wrap gap-2 pt-2">
          <Button size="sm" variant="outline" asChild>
            <Link to="/accounts">Open Accounts</Link>
          </Button>
          <Button size="sm" variant="ghost" asChild>
            <Link to={`/parties/${partyId}/edit`}>Party master</Link>
          </Button>
        </div>
      ) : null}
    </>
  );

  if (presentation === "sheet") {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto" data-testid="ledger-voucher-detail-sheet">
          <SheetHeader>
            <div className="flex flex-wrap items-center gap-2 pr-8">
              <SheetTitle className="font-mono text-base">{title}</SheetTitle>
              {vtype ? <Pill tone={TYPE_TONE[vtype] || "default"}>{vtype}</Pill> : null}
              {ledgerContext?.tally_sync_status ? (
                <TallySyncBadge status={ledgerContext.tally_sync_status} showDot={false} />
              ) : null}
            </div>
          </SheetHeader>
          <div className="mt-4">{body}</div>
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="financial-voucher-brief-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">
            {label || vtype || "Voucher"} · {title}
          </DialogTitle>
          <DialogDescription>
            Settlement calculation with print / PDF.
          </DialogDescription>
        </DialogHeader>
        {body}
      </DialogContent>
    </Dialog>
  );
}

export function PurchaseBriefDialog({ open, onOpenChange, purchaseId, purchaseCode }) {
  const [loading, setLoading] = useState(false);
  const [purchase, setPurchase] = useState(null);

  useEffect(() => {
    if (!open || !purchaseId) {
      setPurchase(null);
      return undefined;
    }
    let cancelled = false;
    setLoading(true);
    api.get(`/purchases/${purchaseId}`)
      .then((r) => {
        if (!cancelled) setPurchase(r.data);
      })
      .catch((err) => {
        if (!cancelled) {
          toast.error(extractError(err));
          setPurchase(null);
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, [open, purchaseId]);

  const vouchers = purchase?.vouchers || [];
  const material = vouchers.find((v) => v.voucher_type === "MATERIAL");
  const freight = vouchers.find((v) => v.voucher_type === "FREIGHT");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[85vh] overflow-y-auto" data-testid="financial-purchase-brief-dialog">
        <DialogHeader>
          <DialogTitle className="font-mono">
            Purchase · {purchase?.code || purchaseCode || "—"}
          </DialogTitle>
          <DialogDescription>
            Quick purchase snapshot so you can stay on this report view.
          </DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading purchase…
          </div>
        ) : (
          <div className="space-y-4">
            <div className="flex flex-wrap items-center gap-2">
              {purchase?.state ? <StatePill state={purchase.state} /> : null}
              {purchase?.lab_verdict ? <Pill tone="muted">Lab {purchase.lab_verdict}</Pill> : null}
              {purchase?.variety ? <Pill tone="sage">{purchase.variety}</Pill> : null}
            </div>

            <div className="rounded-lg border border-border bg-muted/30 px-3 py-2">
              <BriefRow label="Party" value={purchase?.party_name} />
              <BriefRow label="Vehicle" value={purchase?.vehicle_number ? String(purchase.vehicle_number).toUpperCase() : null} />
              <BriefRow label="Invoice" value={purchase?.invoice_no} />
              <BriefRow label="Gate pass" value={purchase?.gate_pass_code} />
              <BriefRow label="Created" value={formatListDate(purchase?.created_at)} />
            </div>

            <div className="rounded-lg border border-border px-3 py-2">
              <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Weights</p>
              <BriefRow label="Gross" value={formatQty(purchase?.gross_weight_qtl)} />
              <BriefRow label="Tare" value={formatQty(purchase?.tare_weight_qtl)} />
              <BriefRow label="Truck net" value={formatQty(purchase?.net_weight_qtl)} />
              <BriefRow label="Party net" value={formatQty(purchase?.party_net_weight_qtl)} />
              <BriefRow label="Bags" value={purchase?.bags_reported ?? "—"} />
              <BriefRow
                label="Rate / qtl"
                value={
                  purchase?.negotiated_rate != null || purchase?.rate_per_qtl != null
                    ? `₹${Number(purchase.negotiated_rate ?? purchase.rate_per_qtl).toLocaleString("en-IN")}`
                    : null
                }
              />
            </div>

            {(material || freight) ? (
              <div className="rounded-lg border border-border px-3 py-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Linked vouchers</p>
                {material ? (
                  <BriefRow
                    label={`Material ${material.code}`}
                    value={formatCurrency(material.net_payable)}
                  />
                ) : null}
                {freight ? (
                  <BriefRow
                    label={`Freight ${freight.code}`}
                    value={formatCurrency(freight.net_payable)}
                  />
                ) : null}
              </div>
            ) : null}

            {purchase?.unloading ? (
              <div className="rounded-lg border border-border px-3 py-2">
                <p className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Unloading</p>
                <BriefRow label="Bags" value={purchase.unloading.total_bags ?? "—"} />
                <BriefRow label="Weight" value={formatQty(purchase.unloading.total_weight_qtl)} />
              </div>
            ) : null}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
